import React, { useState, useEffect } from 'react';
import { IonSpinner } from '@ionic/react';
import { useAdditionalContent } from '@/hooks/useAdditionalContent';
import { cacheBustImageUrl } from '@/utils/imageUtils';

const AUTO_SLIDE_DELAY = 8000; // 8 seconds
const ARROW_PREV =
  'https://api.builder.io/api/v1/image/assets/e7c2d913b4d14422810bbda8f72e0c16/307209d5da576e82bb5b8881666d4eefcfb7f2bd?placeholderIfAbsent=true';
const ARROW_NEXT =
  'https://api.builder.io/api/v1/image/assets/e7c2d913b4d14422810bbda8f72e0c16/61276fd5f267839703471eeeef61bf86b9a056aa?placeholderIfAbsent=true';

export const QuickTips: React.FC = () => {
  const { content, loading } = useAdditionalContent({ page: 'home', slot: 'quick_tips' });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lastManualNav, setLastManualNav] = useState(0);

  const handleCycle = (direction: 'prev' | 'next') => {
    setCurrentIndex((prev) => {
      if (direction === 'next') return prev === content.length - 1 ? 0 : prev + 1;
      return prev === 0 ? content.length - 1 : prev - 1;
    });
    setLastManualNav(Date.now());
  };

  useEffect(() => {
    if (content.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev === content.length - 1 ? 0 : prev + 1));
    }, AUTO_SLIDE_DELAY);
    return () => clearInterval(interval);
  }, [content.length, lastManualNav]);

  const currentItem = content.length > 0 ? content[currentIndex] : null;
  const currentImageSrc = React.useMemo(
    () => (currentItem?.imageUrl ? cacheBustImageUrl(currentItem.imageUrl) : ''),
    [currentItem?.imageUrl, currentItem?.id]
  );

  return (
    <>
      <aside className="justify-start items-start shadow-[0_14px_42px_0_rgba(8,15,52,0.06)] bg-white self-stretch flex min-w-60 w-full flex-col flex-1 shrink basis-[0%] mt-2.5 p-3 rounded-[10px]">
        {content.length > 1 && (
          <div className="flex w-full items-center justify-end gap-[5px] mb-2 flex-shrink-0">
            <button
              onClick={() => handleCycle('prev')}
              className="w-6 h-6 rounded-[50px] hover:scale-110 transition-transform cursor-pointer"
              aria-label="Previous tip"
            >
              <img src={ARROW_PREV} alt="Previous" className="w-6 h-6" />
            </button>
            <button
              onClick={() => handleCycle('next')}
              className="w-6 h-6 rounded-[50px] hover:scale-110 transition-transform cursor-pointer"
              aria-label="Next tip"
            >
              <img src={ARROW_NEXT} alt="Next" className="w-full h-full pointer-events-none" />
            </button>
          </div>
        )}
        <div className="w-full flex-shrink-0 grow">
          {loading ? (
            <div className="flex items-center justify-center p-4">
              <IonSpinner name="crescent" />
            </div>
          ) : currentItem?.imageUrl ? (
            <div
              className="relative w-full h-full rounded-lg overflow-hidden flex items-center justify-center"
              style={{ maxHeight: '450px', minHeight: '250px' }}
            >
              <img
                src={currentImageSrc}
                alt={currentItem.title || 'Quick tip'}
                className="w-full h-full object-contain"
              />
            </div>
          ) : null}
        </div>
      </aside>
    </>
  );
};
